import { resolveDynamicVariable } from './dynamicVariables';

const DEFAULT_ARRAY_LENGTH = 2;
const MAX_REF_DEPTH = 8;

function resolveValueExpression(raw, resolveVar) {
  if (raw == null) return '';
  return String(raw).replace(/\{\{\s*([^}]+)\s*\}\}/g, (match, name) => {
    const trimmed = name.trim();
    if (resolveVar) {
      const scoped = resolveVar(trimmed);
      if (scoped !== null && scoped !== undefined) return String(scoped);
    }
    const dynamic = resolveDynamicVariable(trimmed);
    return dynamic !== null ? dynamic : match;
  });
}

function defaultForType(type, fieldName = '') {
  const name = String(fieldName).toLowerCase();
  if (type === 'integer') {
    if (name.includes('id')) return Math.floor(Math.random() * 10000) + 1;
    return Math.floor(Math.random() * 999);
  }
  if (type === 'number') return parseFloat((Math.random() * 1000).toFixed(2));
  if (type === 'boolean') return Math.random() < 0.5;
  if (type === 'null') return null;
  if (name.includes('email')) return resolveDynamicVariable('$randomEmail');
  if (name.includes('name')) return resolveDynamicVariable('$randomFullName');
  if (name.includes('phone')) return resolveDynamicVariable('$randomPhoneNumber');
  if (name.includes('city')) return resolveDynamicVariable('$randomCity');
  if (name === 'id' || name.endsWith('id')) return resolveDynamicVariable('$randomUUID');
  return resolveDynamicVariable('$randomWord');
}

function coerceType(type, value) {
  if (type === 'null') return null;
  if (type === 'integer') {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : 0;
  }
  if (type === 'number') {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
  }
  if (type === 'boolean') {
    if (value === true || value === false) return value;
    const s = String(value).toLowerCase();
    if (s === 'true') return true;
    if (s === 'false') return false;
    return Boolean(value);
  }
  return value == null ? '' : String(value);
}

function arrayLengthForNode(node) {
  const min =
    typeof node?.minItems === 'number' ? Math.max(0, Math.floor(node.minItems)) : DEFAULT_ARRAY_LENGTH;
  const max =
    typeof node?.maxItems === 'number'
      ? Math.max(min, Math.floor(node.maxItems))
      : min;
  if (max === min) return min;
  return min + Math.floor(Math.random() * (max - min + 1));
}

function resolveLeafValue(node, ctx) {
  const type = node.type || 'string';
  if (Array.isArray(node.enum) && node.enum.length > 0) {
    const pick = node.enum[Math.floor(Math.random() * node.enum.length)];
    return coerceType(type === 'integer' || type === 'number' || type === 'boolean' ? type : 'string', pick);
  }
  const raw = String(node.value ?? '').trim();
  if (raw) {
    const resolved = resolveValueExpression(raw, ctx.resolveVar);
    if (!/\{\{[^}]+\}\}/.test(resolved)) {
      return coerceType(type, resolved);
    }
  }
  return coerceType(type, defaultForType(type, node.name));
}

function generateFromNodes(nodes, ctx) {
  const out = {};
  for (const node of nodes || []) {
    const key = String(node.name ?? '').trim();
    if (!key) continue;
    out[key] = generateNodeValue(node, ctx);
  }
  return out;
}

function generateArrayFromNodes(nodes, ctx, length = DEFAULT_ARRAY_LENGTH) {
  const items = [];
  const count = Math.max(0, length);
  for (let i = 0; i < count; i += 1) {
    items.push(generateFromNodes(nodes, ctx));
  }
  return items;
}

function generateNodeValue(node, ctx) {
  const type = node?.type || 'string';

  if (type === 'object') {
    return generateFromNodes(node.children, ctx);
  }

  if (type === 'array') {
    const kids = node.children || [];
    const len = arrayLengthForNode(node);
    if (kids.length === 1) {
      const template = kids[0];
      const items = [];
      for (let i = 0; i < len; i += 1) {
        items.push(generateNodeValue(template, ctx));
      }
      return items;
    }
    if (kids.length > 1) {
      return generateArrayFromNodes(kids, ctx, len);
    }
    return [];
  }

  if (type === 'ref') {
    const refId = node.refSchemaId;
    if (!refId || ctx.refStack.has(String(refId)) || ctx.refStack.size >= MAX_REF_DEPTH) {
      return null;
    }
    const item = ctx.schemasById.get(String(refId));
    if (!item) return null;
    ctx.refStack.add(String(refId));
    try {
      return generateSchemaMock({
        rootType: item.rootType,
        nodes: item.nodes,
        schemasById: ctx.schemasById,
        resolveVar: ctx.resolveVar,
        refStack: ctx.refStack,
      });
    } finally {
      ctx.refStack.delete(String(refId));
    }
  }

  return resolveLeafValue(node, ctx);
}

/**
 * Generate mock JSON from a schema definition (preview + mock dataset build; Rust port planned).
 */
export function generateSchemaMock({
  rootType = 'object',
  nodes = [],
  schemas = null,
  schemasById: schemasByIdInput = null,
  resolveVar = null,
  refStack = null,
} = {}) {
  const schemasById = schemasByIdInput || new Map();
  if (!schemasByIdInput && schemas?.items) {
    for (const item of schemas.items) {
      schemasById.set(String(item.id), item);
    }
  }

  const ctx = {
    schemasById,
    resolveVar,
    refStack: refStack || new Set(),
  };

  if (rootType === 'array') {
    return generateArrayFromNodes(nodes, ctx);
  }
  return generateFromNodes(nodes, ctx);
}
